// ReportContent.js
import React from 'react';
import ReportHeader from './ReportHeader';
import ReportFooter from './ReportFooter';
import ReportTestReferenceContent from './ReportTestReferenceContent';
import HumanGrid from './HumanGrid';

const ReportContent = React.forwardRef((props, ref) => {
    const {
      pscore,
      apProbability,
      metastasesProb,
      pcsmProb,
    //   nccnRisk,
      percentile,
      formData,
    } = props;

    // Perform calculations here
    const clampToZero = (value) => {
      if (value == null) {
        return 'N/A';
      }
      return Math.max(0, value).toFixed(2);
    }

    const clampToZeroNoDecimals = (value) => {
      if (value == null) {
        return 'N/A';
      }
      // Clamp negative values to 0, then round to nearest integer
      return Math.max(0, value).toFixed(0);
    }
    
    const apProbabilityValue = clampToZero(apProbability?.probability);
    const apCiLowerValue = clampToZero(apProbability?.ciLower);
    const apCiUpperValue = clampToZero(apProbability?.ciUpper);


    const apProbabilityAvailable = apProbability && typeof apProbability.probability === 'number';

    // const pcsm10YearCiLower = clampToZero(pcsmProb[10]?.confidence_interval?.lower);
    // const pcsm10YearCiUpper = clampToZero(pcsmProb[10]?.confidence_interval?.upper);
    // const pcsm10YearValue   = clampToZero(pcsmProb[10]?.pcsm_mean_probability);


    const pcsm10YearCiLower = clampToZero(pcsmProb?.[10]?.confidence_interval?.lower);
    const pcsm10YearCiUpper = clampToZero(pcsmProb?.[10]?.confidence_interval?.upper);
    const pcsm10YearValue   = clampToZero(pcsmProb?.[10]?.pcsm_mean_probability);


    const circleClassName = 
      pscore <= 4 ? 'green-circle' :
      pscore === 5 ? 'orange-circle' :
      'red-circle';



    // const circleClassName = pscore <= 5 ? 'green-circle' : 'red-circle';

    const roundPercentile = Math.round(parseFloat(percentile));
    const adjustedPercentile = Math.round(100 - parseFloat(percentile));

    return (
      <div className="report-container" ref={ref}>
        {/* report content goes here */}
        <ReportHeader formData={formData} />
        <div className="result-row">
          <span className="result-heading">Result:</span>

          <span
            className={
              pscore <= 4
                ? 'low-risk'
                : pscore === 5
                  ? 'intermediate-risk'
                  : 'high-risk'
            }
          >
            {pscore <= 4
              ? 'Low Risk'
              : pscore === 5
                ? 'Intermediate Risk'
                : 'High Risk'}
          </span>



          {/* <span className={pscore <= 5 ? 'low-risk' : 'high-risk'}>
            {pscore <= 5 ? 'Low Risk' : 'High Risk'}
          </span> */}
        </div>

        <p className="classifier-description">
          The Prostatype Genomic Classifier (PGC<sup style={{ fontSize: "0.5em"}}>TM</sup>) provides prognosis and decision support in treatment
          selection for prostate cancer patients considering Active Surveillance. These results provide
          for the likelihood of adverse pathology at radical prostatectomy, the likelihood of metastasis
          at 10 years and the likelihood of prostate cancer specific mortality at 10 years
          <span className="underline"> in the absence of curative intent treatment</span>.
        </p>

        <div className="report-columns-container">
          <div className="report-column">
            <h2>Report Interpretation:</h2>
            <div className="report-interpretation">
              <h3>Prostate Cancer Specific Mortality Likelihood:</h3>
              {/* {pcsmProb && pcsmProb[5] && pcsmProb[10] ? ( */}
              {pcsmProb && pcsmProb[10] ? (
              

                <div>
                  {/* <p className="bullet-point">
                    At 5 years: {pcsmProb[5]?.pcsm_mean_probability?.toFixed(2)}%
                    (95% CI: {pcsmProb[5]?.confidence_interval?.lower?.toFixed(2)} -{' '}
                    {pcsmProb[5]?.confidence_interval?.upper?.toFixed(2)})
                  </p> */}
                  <p className="bullet-point">
                    At 10 years: {pcsmProb[10]?.pcsm_mean_probability?.toFixed(2)}%
                    (95% CI: {pcsm10YearCiLower} - {pcsm10YearCiUpper})
                    {/* (95% CI: {pcsmProb[10]?.confidence_interval?.lower?.toFixed(2)} -{' '}
                    {pcsmProb[10]?.confidence_interval?.upper?.toFixed(2)}) */}
                  </p>
                </div>
              ) : (
                <p>No PCSM probability available</p>
              )}
              <h3>Metastasis Likelihood:</h3>
              {/* {metastasesProb !== undefined && metastasesProb !== null ? ( */}
              {metastasesProb && metastasesProb[5] && metastasesProb[10] ? (

                <div>
                  <p className="bullet-point">
                    At 5 years: {clampToZero(metastasesProb[5]?.metastases_mean_probability)}%
                    (95% CI: {clampToZero(metastasesProb[5]?.confidence_interval?.lower)} -{' '}
                    {clampToZero(metastasesProb[5]?.confidence_interval?.upper)})
                  </p>
                  <p className="bullet-point">
                    At 10 years: {clampToZero(metastasesProb[10]?.metastases_mean_probability)}%
                    (95% CI: {clampToZero(metastasesProb[10]?.confidence_interval?.lower)} -{' '}
                    {clampToZero(metastasesProb[10]?.confidence_interval?.upper)})
                  </p>
                  <p className="bullet-point">
                    This means approximately{' '}
                    {clampToZeroNoDecimals(metastasesProb[10]?.metastases_mean_probability)} in 100 patients with this PGC
                    result are expected to experience metastasis within 10 years if he undergoing active surveillance.
                  </p>
                  <p className="bullet-point">
                    Metastases are those detected by conventional imaging (e.g., CT, MRI, radionuclide
                    bone scan and exclusive of advanced imaging such as PET scans).
                  </p>
                </div>
              ) : (
                <p>No metastases development probability available</p>
              )}


              <h3>Adverse Pathology Likelihood at Radical Prostatectomy:</h3>
              {apProbability !== undefined && apProbability.probability !== null ? (
                <div>
                  {/* <p className="bullet-point">
                    {apProbabilityValue}% (95% CI: {apCiLowerValue} - {apCiUpperValue})
                  </p>
                  <p className="bullet-point">
                    Adverse Pathology is defined as Grade Group ≥ 3 and/or pathologic stage T3-4.
                  </p> */}

                  {apProbabilityAvailable ? (
                    <p className="bullet-point">
                      {apProbabilityValue}% (95% CI: {apCiLowerValue} - {apCiUpperValue})
                    </p>
                  ) : (
                    <p>No AP probability available</p>
                  )}
                  <p className="bullet-point">
                    Adverse Pathology is defined as Grade Group ≥ 3 and/or pathologic stage T3-4.
                  </p>


                </div>
              ) : (
                <p>No AP probability available</p>
              )}
            </div>
          </div>




          <div className="report-column-fig">
            <h2>
              10 year Likelihood of <br /> Prostate Cancer Specific Mortality:
            </h2>

            <div className={circleClassName}>
              <div className="percentage">{pcsm10YearValue}%</div>
              <div className="confidence-interval">
                (95% CI: {pcsm10YearCiLower} - {pcsm10YearCiUpper})
              </div>
            </div>
            <HumanGrid riskPercentage={parseFloat(pcsm10YearValue)} />
          </div>
        </div>

        <div className="percentile-bar-container avoid-page-break">
          <p className="percentile-description">
            {adjustedPercentile}% of patients with a similar PSA, Grade Group and Clinical T-stage had a higher PGC
            score indicating a higher likelihood of Prostate Cancer Specific Mortality*
          </p>
          <div className="percentile-bar">
            <div className="percentile-triangle" style={{ left: `${roundPercentile}%` }}>
              <span>
                {roundPercentile}
                <sup style={{ fontSize: "0.5em" }}>th</sup> Percentile
              </span>
            </div>
            <div className="percentile-scale">
              <span>0</span>
              <span>50</span>
              <span>100</span>
            </div>
          </div>
        </div>

        <div className="footer-placeholder"></div>

        <ReportFooter />

        <div className="page-break"></div>

        <div className="report-second-page-container">
         
          <ReportHeader formData={formData} />
          <ReportTestReferenceContent />
          <div className="avoid-page-break-after">
          

            <ReportFooter />
          </div>
        </div>
        {/* <div className="placeholder"></div> */}
      </div>
    );
  
})

export default ReportContent;
